import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Route, CheckCircle2, XCircle, ArrowRight, Zap, RefreshCw, Shield, GitBranch } from "lucide-react";
import ResellerLayout from "@/shared/components/reseller/ResellerLayout";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { getProviderName as getProviderNameUtil } from "@/shared/utils";

function RuleCard({ rule, index, getProviderName, t }) {
  const [hov, setHov] = useState(false);
  const isActive = rule.status === "active";
  const providerSeq = (() => {
    try { return rule.provider_sequence ? JSON.parse(rule.provider_sequence) : []; }
    catch { return []; }
  })();

  const typeColor = {
    deposit:    { color:"#10b981", bg:"rgba(16,185,129,.12)",  border:"rgba(16,185,129,.25)"  },
    withdrawal: { color:"#f59e0b", bg:"rgba(245,158,11,.12)",  border:"rgba(245,158,11,.25)"  },
  }[rule.transaction_type] || { color:"#38bdf8", bg:"rgba(56,189,248,.12)", border:"rgba(56,189,248,.25)" };

  return (
    <motion.div
      initial={{ opacity:0, y:24 }}
      animate={{ opacity:1, y:0 }}
      transition={{ delay: index * 0.07, duration:.42, ease:[0.34,1.56,0.64,1] }}
      onMouseEnter={()=>setHov(true)}
      onMouseLeave={()=>setHov(false)}
      style={{
        background: hov
          ? "linear-gradient(135deg,rgba(15,32,60,.96) 0%,rgba(18,36,65,.96) 100%)"
          : "linear-gradient(135deg,rgba(13,27,46,.88) 0%,rgba(15,30,55,.88) 100%)",
        border: `1px solid ${hov ? "rgba(56,189,248,.28)" : "rgba(56,189,248,.1)"}`,
        borderRadius:"22px",
        padding:"24px",
        position:"relative",
        overflow:"hidden",
        transition:"all .42s cubic-bezier(0.34,1.56,0.64,1)",
        transform: hov ? "translateY(-7px) scale(1.015)" : "translateY(0) scale(1)",
        boxShadow: hov
          ? "0 22px 54px rgba(0,0,0,.42), 0 0 0 1px rgba(56,189,248,.18)"
          : "0 4px 18px rgba(0,0,0,.22)",
      }}
    >
      {/* Top gradient line on hover */}
      {hov && <div style={{ position:"absolute", top:0, left:0, right:0, height:"2px", background:"linear-gradient(90deg,transparent,rgba(56,189,248,.6),rgba(99,102,241,.5),transparent)" }} />}

      {/* Radial glow */}
      <div style={{ position:"absolute", inset:0, background: hov ? "radial-gradient(circle at 90% 10%, rgba(56,189,248,.07) 0%, transparent 60%)" : "none", pointerEvents:"none", transition:"all .4s ease" }} />

      {/* Priority badge — top right */}
      <div style={{ position:"absolute", top:"18px", right:"18px", display:"flex", alignItems:"center", gap:"5px", padding:"4px 10px", borderRadius:"20px", background:"rgba(56,189,248,.08)", border:"1px solid rgba(56,189,248,.18)", fontSize:"10px", fontWeight:700, color:"#38bdf8", letterSpacing:"0.08em" }}>
        <Shield style={{ width:"10px", height:"10px" }} />
        P{rule.priority}
      </div>

      {/* Header row */}
      <div style={{ display:"flex", alignItems:"center", gap:"14px", marginBottom:"18px" }}>
        <div style={{
          width:"46px", height:"46px", borderRadius:"14px",
          background: isActive ? "linear-gradient(135deg,rgba(16,185,129,.25),rgba(56,189,248,.2))" : "rgba(100,116,139,.1)",
          border: `1px solid ${isActive ? "rgba(16,185,129,.3)" : "rgba(100,116,139,.2)"}`,
          display:"flex", alignItems:"center", justifyContent:"center",
          transition:"transform .38s cubic-bezier(0.34,1.56,0.64,1)",
          transform: hov ? "rotate(-6deg) scale(1.12)" : "none",
          flexShrink:0,
        }}>
          <Route style={{ width:"20px", height:"20px", color: isActive ? "#10b981" : "#64748b" }} />
        </div>
        <div style={{ flex:1, minWidth:0 }}>
          <h3 style={{ margin:0, fontSize:"15px", fontWeight:700, color:"#f0f9ff" }}>{rule.name}</h3>
          <p style={{ margin:"3px 0 0", fontSize:"12px", color:"#475569" }}>{t("priority")||"Priority"}: {rule.priority}</p>
        </div>
      </div>

      {/* Badge row */}
      <div style={{ display:"flex", flexWrap:"wrap", gap:"7px", marginBottom:"20px" }}>
        {/* Status */}
        <span style={{ display:"inline-flex", alignItems:"center", gap:"5px", padding:"4px 10px", borderRadius:"20px", background: isActive ? "rgba(16,185,129,.12)" : "rgba(100,116,139,.1)", border: `1px solid ${isActive ? "rgba(16,185,129,.28)" : "rgba(100,116,139,.2)"}`, fontSize:"11px", fontWeight:600, color: isActive ? "#10b981" : "#64748b" }}>
          {isActive ? <CheckCircle2 style={{ width:"11px", height:"11px" }} /> : <XCircle style={{ width:"11px", height:"11px" }} />}
          {isActive ? (t("active")||"Active") : (t("inactive")||"Inactive")}
        </span>

        {/* Type */}
        <span style={{ padding:"4px 10px", borderRadius:"20px", background:typeColor.bg, border:`1px solid ${typeColor.border}`, fontSize:"11px", fontWeight:600, color:typeColor.color }}>
          {rule.transaction_type === "deposit" ? (t("deposit")||"Deposit") : rule.transaction_type === "withdrawal" ? (t("withdrawal")||"Withdrawal") : (t("allTypes")||"All Types")}
        </span>

        {/* Method */}
        {rule.payment_method && (
          <span style={{ padding:"4px 10px", borderRadius:"20px", background:"rgba(99,102,241,.1)", border:"1px solid rgba(99,102,241,.22)", fontSize:"11px", fontWeight:600, color:"#818cf8" }}>
            {rule.payment_method.replace(/_/g," ")}
          </span>
        )}

        {/* Mode */}
        {rule.routing_mode && (
          <span style={{ display:"inline-flex", alignItems:"center", gap:"4px", padding:"4px 10px", borderRadius:"20px", background:"rgba(56,189,248,.08)", border:"1px solid rgba(56,189,248,.18)", fontSize:"11px", fontWeight:600, color:"#38bdf8" }}>
            <GitBranch style={{ width:"10px", height:"10px" }} />
            {rule.routing_mode}
          </span>
        )}

        {/* Failover */}
        {rule.failover_enabled && (
          <span style={{ display:"inline-flex", alignItems:"center", gap:"4px", padding:"4px 10px", borderRadius:"20px", background:"rgba(245,158,11,.1)", border:"1px solid rgba(245,158,11,.25)", fontSize:"11px", fontWeight:600, color:"#fbbf24" }}>
            <Zap style={{ width:"10px", height:"10px" }} />{t("failover")||"Failover"}
          </span>
        )}
      </div>

      {/* Provider Sequence */}
      {providerSeq.length > 0 && (
        <div style={{ marginBottom:"16px" }}>
          <p style={{ fontSize:"10px", color:"#475569", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:"10px" }}>
            {t("providerSequence")||"Provider Sequence"}
          </p>
          <div style={{ display:"flex", flexWrap:"wrap", alignItems:"center", gap:"6px" }}>
            {providerSeq.map((code, idx) => (
              <React.Fragment key={code}>
                <span style={{
                  padding:"6px 12px", borderRadius:"10px",
                  background: hov ? "rgba(56,189,248,.15)" : "rgba(56,189,248,.08)",
                  border:"1px solid rgba(56,189,248,.22)",
                  fontSize:"12px", fontWeight:600, color:"#7dd3fc",
                  transition:"all .3s cubic-bezier(0.34,1.56,0.64,1)",
                  transform: hov ? "scale(1.04)" : "scale(1)",
                }}>
                  {getProviderName(code)}
                </span>
                {idx < providerSeq.length - 1 && (
                  <ArrowRight style={{ width:"14px", height:"14px", color:"#334155" }} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}

      {/* Amount Range */}
      {(rule.min_amount > 0 || rule.max_amount < 999999999) && (
        <div style={{ paddingTop:"14px", borderTop:"1px solid rgba(56,189,248,.06)" }}>
          <p style={{ fontSize:"11px", color:"#64748b", margin:0 }}>
            <span style={{ color:"#475569", fontWeight:600 }}>{t("amountRange")||"Range"}:</span>{" "}
            <span style={{ color:"#38bdf8", fontFamily:"monospace" }}>฿{rule.min_amount?.toLocaleString()}</span>
            {" "}—{" "}
            <span style={{ color:"#38bdf8", fontFamily:"monospace" }}>฿{rule.max_amount?.toLocaleString()}</span>
          </p>
        </div>
      )}
    </motion.div>
  );
}

export default function ResellerRouting() {
  const resellerName = "Asia Gaming Network";
  const { language, t } = useLanguage();

  const { data: rules = [], isLoading, refetch } = useQuery({
    queryKey: ["routing-rules"],
    queryFn: async () => [],
  });

  const { data: providers = [] } = useQuery({
    queryKey: ["providers"],
    queryFn: async () => [],
  });

  const getProviderName = (code) => {
    const p = providers.find(p => p.code === code);
    return p ? getProviderNameUtil(p, language) : code;
  };

  const activeRules = rules.filter(r => r.status === "active").length;

  return (
    <ResellerLayout currentPage="ResellerRouting" resellerName={resellerName}>
      <style>{`@keyframes rrspin{to{transform:rotate(360deg)}}`}</style>
      <div style={{ maxWidth:"1400px" }}>

        {/* Header */}
        <motion.div initial={{ opacity:0, y:-12 }} animate={{ opacity:1, y:0 }} style={{ marginBottom:"28px" }}>
          <div style={{ display:"flex", flexWrap:"wrap", alignItems:"center", justifyContent:"space-between", gap:"16px" }}>
            <div style={{ display:"flex", alignItems:"center", gap:"14px" }}>
              <div style={{ width:"44px", height:"44px", borderRadius:"13px", background:"linear-gradient(135deg,#6366f1,#38bdf8)", display:"flex", alignItems:"center", justifyContent:"center", boxShadow:"0 4px 18px rgba(99,102,241,.45)" }}>
                <Route style={{ width:"20px", height:"20px", color:"#fff" }} />
              </div>
              <div>
                <h1 style={{ fontSize:"24px", fontWeight:800, color:"#f0f9ff", letterSpacing:"-0.02em", margin:0 }}>
                  {t("routingRulesTitle")||"Routing Rules"}
                </h1>
                <p style={{ fontSize:"13px", color:"#475569", margin:0 }}>
                  {activeRules} active · {rules.length} total — {t("viewTransactionRoutingConfig")||"View transaction routing configuration"}
                </p>
              </div>
            </div>
            <button
              onClick={() => refetch()}
              style={{ display:"flex", alignItems:"center", gap:"8px", padding:"10px 16px", borderRadius:"12px", background:"rgba(99,102,241,.08)", border:"1px solid rgba(99,102,241,.2)", color:"#818cf8", fontSize:"13px", fontWeight:600, cursor:"pointer", transition:"all .35s cubic-bezier(0.34,1.56,0.64,1)" }}
              onMouseEnter={e=>{ e.currentTarget.style.background="rgba(99,102,241,.16)"; e.currentTarget.style.transform="scale(1.05)"; }}
              onMouseLeave={e=>{ e.currentTarget.style.background="rgba(99,102,241,.08)"; e.currentTarget.style.transform="scale(1)"; }}
            >
              <RefreshCw style={{ width:"14px", height:"14px" }} />Refresh
            </button>
          </div>
        </motion.div>

        {/* Content */}
        {isLoading ? (
          <div style={{ textAlign:"center", padding:"80px 24px", color:"#475569" }}>
            <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:"16px" }}>
              <RefreshCw style={{ width:"32px", height:"32px", color:"#6366f1", animation:"rrspin 1s linear infinite" }} />
              <span style={{ fontSize:"14px" }}>{t("loading")||"Loading…"}</span>
            </div>
          </div>
        ) : rules.length === 0 ? (
          <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }}
            style={{ textAlign:"center", padding:"80px 24px", background:"linear-gradient(135deg,rgba(13,27,46,.8) 0%,rgba(15,30,55,.8) 100%)", border:"1px solid rgba(99,102,241,.1)", borderRadius:"22px" }}>
            <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:"16px" }}>
              <Route style={{ width:"56px", height:"56px", color:"#1e293b" }} />
              <p style={{ margin:0, fontSize:"14px", color:"#475569" }}>{t("noRoutingRules")||"No routing rules configured"}</p>
            </div>
          </motion.div>
        ) : (
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(340px,1fr))", gap:"18px" }}>
            {rules.map((rule, i) => (
              <RuleCard key={rule.id} rule={rule} index={i} getProviderName={getProviderName} t={t} />
            ))}
          </div>
        )}
      </div>
    </ResellerLayout>
  );
}